import React from 'react';
import { AnimatePresence } from 'framer-motion';
import ConfirmDialog from '../../components/dialogs/ConfirmDialog';
import SaveSchemaAsDraftDialog from '../../components/dialogs/SaveSchemaAsDraftDialog';
import SaveSchemaAsPublishedDialog from '../../components/dialogs/SaveSchemaAsPublishedDialog';
import ImportConflictDialog from '../../components/dialogs/ImportConflictDialog';
import { UnifiedSchema, EventSchema, Conflict, NonStandardField, ImportResolution } from '../../types/schema';

interface SchemaEditorDialogsProps {
  showConfirmDialog: boolean;
  eventToDelete: EventSchema | null;
  cancelDeleteEvent: () => void;
  confirmDeleteEvent: () => void;
  showConfirmCancelDialog: boolean;
  cancelConfirmCancelDialog: () => void;
  confirmCancel: () => void;
  showSaveAsDraftDialog: boolean;
  closeSaveAsDraftDialog: () => void;
  currentSchema: UnifiedSchema | null;
  onSaveAsDraftSuccess: (newSchema: UnifiedSchema) => void;
  allSchemaNames: string[];
  showSaveAsPublishedDialog: boolean;
  closeSaveAsPublishedDialog: () => void;
  onSaveAsPublishedSuccess: (newSchema: UnifiedSchema) => void;
  showDeletePropertyConfirmation: boolean;
  propertyToDeleteFullPath: string | null;
  cancelDeleteProperty: () => void;
  confirmDeleteProperty: () => void;
  showImportConflictDialog: boolean;
  closeImportConflictDialog: () => void;
  conflictingParameters: Conflict[];
  nonStandardFields: NonStandardField[];
  onImportResolution: (resolution: ImportResolution) => Promise<void>;
  showPersistenceConflictDialog: boolean;
  handleConfirmLoadBackend: () => void;
  handleConfirmLoadPersisted: () => void;
}

const SchemaEditorDialogs: React.FC<SchemaEditorDialogsProps> = ({
  showConfirmDialog,
  eventToDelete,
  cancelDeleteEvent,
  confirmDeleteEvent,
  showConfirmCancelDialog,
  cancelConfirmCancelDialog,
  confirmCancel,
  showSaveAsDraftDialog,
  closeSaveAsDraftDialog,
  currentSchema,
  onSaveAsDraftSuccess,
  allSchemaNames,
  showSaveAsPublishedDialog,
  closeSaveAsPublishedDialog,
  onSaveAsPublishedSuccess,
  showDeletePropertyConfirmation,
  propertyToDeleteFullPath,
  cancelDeleteProperty,
  confirmDeleteProperty,
  showImportConflictDialog,
  closeImportConflictDialog,
  conflictingParameters,
  nonStandardFields,
  onImportResolution,
  showPersistenceConflictDialog,
  handleConfirmLoadBackend,
  handleConfirmLoadPersisted,
}) => {
  return (
    <AnimatePresence>
      {showPersistenceConflictDialog && (
        <ConfirmDialog
          onClose={handleConfirmLoadBackend} // Default to loading backend if user closes
          onConfirm={handleConfirmLoadPersisted}
          title="Unsaved Changes Detected"
          message="It looks like you have unsaved changes from your last session. Do you want to load these changes or discard them and load the last saved version?"
          confirmText="Load Unsaved Changes"
          cancelText="Discard & Load Saved"
          isDanger={false} // Not a destructive action, just a choice
          cancelAction={handleConfirmLoadBackend}
        />
      )}
      {showConfirmDialog && eventToDelete && (
        <ConfirmDialog
          onClose={cancelDeleteEvent}
          onConfirm={confirmDeleteEvent}
          title="Confirm Event Deletion"
          message={`Are you sure you want to delete the event "${eventToDelete.name}"? This action cannot be undone.`}
          confirmText="Delete"
          isDanger={true}
        />
      )}
      {showConfirmCancelDialog && (
        <ConfirmDialog
          onClose={cancelConfirmCancelDialog}
          onConfirm={confirmCancel}
          title="Unsaved Changes"
          message="You have unsaved changes. Are you sure you want to discard them and exit?"
          confirmText="Discard Changes"
          isDanger={true}
          cancelText="Cancel"
          cancelAction={cancelConfirmCancelDialog}
        />
      )}
      {showSaveAsDraftDialog && (
        <SaveSchemaAsDraftDialog
          open={showSaveAsDraftDialog}
          onClose={closeSaveAsDraftDialog}
          currentSchema={currentSchema}
          onSaveSuccess={onSaveAsDraftSuccess}
          existingSchemaNames={allSchemaNames}
        />
      )}
      {showSaveAsPublishedDialog && (
        <SaveSchemaAsPublishedDialog
          open={showSaveAsPublishedDialog}
          onClose={closeSaveAsPublishedDialog}
          currentSchema={currentSchema}
          onSaveSuccess={onSaveAsPublishedSuccess}
          existingSchemaNames={allSchemaNames}
        />
      )}
      {showDeletePropertyConfirmation && propertyToDeleteFullPath && (
        <ConfirmDialog
          onClose={cancelDeleteProperty}
          onConfirm={confirmDeleteProperty}
          title="Delete Property?"
          message={`The property "${propertyToDeleteFullPath}" is no longer associated with any event. Do you want to delete it entirely from the schema?`}
          confirmText="Delete Property"
          cancelText="Keep Property"
          isDanger={true}
        />
      )}
      {showImportConflictDialog && (
        <ImportConflictDialog
          open={showImportConflictDialog}
          onClose={closeImportConflictDialog}
          conflictingParameters={conflictingParameters}
          nonStandardFields={nonStandardFields}
          onResolve={onImportResolution}
        />
      )}
    </AnimatePresence>
  );
};

export default SchemaEditorDialogs;
